import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Copy, Gift } from "lucide-react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import Reveal from "@/components/Reveal";
import useSEO from "@/lib/useSEO";

export default function Account() {
  useSEO("Your Account | Fleece The Regime", "Your orders and account.", true);
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? { user: location.state.user } : null);
  const [orders, setOrders] = useState([]);
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [referral, setReferral] = useState(null);
  const [emailVerified, setEmailVerified] = useState(true);
  const [resendBusy, setResendBusy] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [setupMode, setSetupMode] = useState(false);
  const [checkEmail, setCheckEmail] = useState("");

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get("google_error")) {
      setFormError("Google sign-in didn't complete. Try again or use email below.");
    }
    if (q.get("verified") === "1") {
      toast.success("Email confirmed — your order history is unlocked. Sign in to see it.");
      setEmailVerified(true);
    } else if (q.get("verified") === "0") {
      toast.error("That confirmation link didn't work — request a fresh one below.");
    } else if (q.get("verified") === "expired") {
      toast.error("That confirmation link expired (24 hours) — request a fresh one below.");
    }
    if (q.get("reset") || q.get("setup")) {
      setResetToken(q.get("reset") || q.get("setup"));
      setMode("reset");
      setSetupMode(Boolean(q.get("setup")));
    }
  }, [location.search]);

  useEffect(() => {
    if (location.state?.user) return;
    api
      .get("/customer/me")
      .then((r) => setAuth(r.data?.customer ? { user: r.data.customer } : false))
      .catch(() => setAuth(false));
  }, [location.state]);

  useEffect(() => {
    if (auth && auth.user) {
      api.get("/customer/orders").then((r) => {
        setOrders(r.data.orders || []);
        setEmailVerified(r.data.email_verified !== false);
      }).catch(() => {});
      api.get("/referrals/me").then((r) => setReferral(r.data)).catch(() => {});
    }
  }, [auth]);

  const resendVerification = async () => {
    setResendBusy(true);
    try {
      await api.post("/customer/resend-verification");
      toast.success("Confirmation email sent — check your inbox and spam.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResendBusy(false);
    }
  };

  const submitEmailAuth = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError("");
    try {
      if (mode === "forgot") {
        await api.post("/customer/forgot-password", { email: form.email });
        toast.success("If that address has an account, a recovery link is on its way — it resets your password or finishes setting up your account.");
        setMode("login");
        return;
      }
      if (mode === "reset") {
        await api.post("/customer/reset-password", { token: resetToken, new_password: form.password });
        toast.success("Password set — every other session was signed out. Sign in with your new password.");
        setMode("login");
        setForm((f) => ({ ...f, password: "" }));
        return;
      }
      const endpoint = mode === "login" ? "/customer/login" : "/customer/register";
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : { name: form.name, email: form.email };
      const r = await api.post(endpoint, payload);
      if (mode === "register") {
        // credentials are established via the emailed link — no instant session
        setCheckEmail(r.data.email || form.email);
        return;
      }
      setAuth({ user: r.data });
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.post("/customer/logout").catch(() => {});
    setAuth(false);
    setOrders([]);
    setMode("login");
    setForm({ name: "", email: "", password: "" });
    setFormError("");
  };

  if (auth === null) {
    return <main className="pt-32 px-6 min-h-screen font-mono text-sm text-muted-foreground">Checking your credentials…</main>;
  }

  if (auth === false) {
    return (
      <main data-testid="account-signin" className="pt-16 min-h-screen flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md border-2 border-black bg-background p-8 shadow-hard-lg text-center">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary">Members only</p>
          <h1 className="mt-3 font-display uppercase text-4xl tracking-tighter">Your orders,<br />your stash.</h1>
          <p className="mt-4 font-mono text-sm text-black/60 leading-relaxed">
            Sign in to see your orders and track your gear from print queue to doorstep.
          </p>
          {checkEmail ? (
            <div data-testid="check-email-panel" className="mt-8 border-2 border-black bg-accent/60 p-5 text-left">
              <p className="font-mono text-sm font-bold uppercase tracking-wider">Check your inbox</p>
              <p className="mt-2 font-mono text-xs text-black/70">
                We emailed <span className="font-bold">{checkEmail}</span> a link that confirms the address is yours and lets you set your password. It expires in 24 hours.
              </p>
              <button
                onClick={() => { setCheckEmail(""); setMode("login"); }}
                data-testid="check-email-back"
                className="mt-4 font-mono text-xs font-bold uppercase tracking-widest text-black/50 underline underline-offset-4 hover:text-primary"
              >
                Back to sign in
              </button>
            </div>
          ) : (
          <>
          <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <div className="mt-8 flex justify-center" data-testid="google-signin-button">
              <GoogleLogin
                ux_mode="redirect"
                login_uri={`${window.location.origin}/api/auth/google/callback`}
                theme="filled_black"
                size="large"
                width="320"
                text="signin_with"
                shape="rectangular"
              />
            </div>
          </GoogleOAuthProvider>
          <p className="mt-4 font-mono text-[11px] text-black/40">One click. No passwords. Google handles the bouncer work.</p>

          <div className="my-6 flex items-center gap-3" aria-hidden>
            <span className="h-0.5 flex-1 bg-black/20" />
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-black/40">or the old-fashioned way</span>
            <span className="h-0.5 flex-1 bg-black/20" />
          </div>

          <form onSubmit={submitEmailAuth} className="text-left" data-testid="email-auth-form">
            {mode === "register" && (
              <div className="mb-3">
                <label htmlFor="account-name" className="mb-2 block font-mono text-xs font-bold uppercase tracking-[0.2em]">Name</label>
                <input
                  id="account-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Name"
                  data-testid="name-input"
                  className="w-full border-2 border-black bg-background px-4 py-3 font-mono text-sm placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
            {mode !== "reset" && (
              <div className="mb-3">
                <label htmlFor="account-email" className="mb-2 block font-mono text-xs font-bold uppercase tracking-[0.2em]">Email</label>
                <input
                  id="account-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="Email"
                  data-testid="email-input"
                  className="w-full border-2 border-black bg-background px-4 py-3 font-mono text-sm placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
            {mode !== "forgot" && mode !== "register" && (
              <div>
                <label htmlFor="account-password" className="mb-2 block font-mono text-xs font-bold uppercase tracking-[0.2em]">
                  {mode === "reset" ? (setupMode ? "Create your password" : "New password") : "Password"}
                </label>
                <input
                  id="account-password"
                  type="password"
                  required
                  minLength={mode === "login" ? 1 : 8}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder={mode === "login" ? "Password" : "Password (8+ characters)"}
                  data-testid="password-input"
                  className="w-full border-2 border-black bg-background px-4 py-3 font-mono text-sm placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {mode === "reset" && setupMode && (
                  <p className="mt-2 font-mono text-[11px] text-black/60">Email confirmed. Set the password only you know — any earlier one is gone.</p>
                )}
              </div>
            )}
            {mode === "login" && (
              <button
                type="button"
                onClick={() => { setMode("forgot"); setFormError(""); }}
                data-testid="forgot-password-link"
                className="mt-2 font-mono text-[11px] font-bold uppercase tracking-widest text-black/50 underline underline-offset-4 hover:text-primary"
              >
                Forgot password?
              </button>
            )}
            {formError && (
              <p className="mt-3 font-mono text-xs font-bold text-red-700" data-testid="auth-error">{formError}</p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="email-auth-submit"
              className="mt-4 w-full border-2 border-black bg-primary py-4 font-display uppercase text-xl text-white shadow-hard btn-press hover:bg-black disabled:opacity-50"
            >
              {busy ? "Hold on…" : mode === "login" ? "Sign in" : mode === "forgot" ? "Email me a recovery link" : mode === "reset" ? (setupMode ? "Create my password" : "Set new password") : "Email me my setup link"}
            </button>
          </form>
          {(mode === "forgot" || mode === "reset") && (
            <button
              onClick={() => { setMode("login"); setFormError(""); }}
              data-testid="auth-back-to-login"
              className="mt-4 font-mono text-xs font-bold uppercase tracking-widest text-black/50 underline underline-offset-4 hover:text-primary"
            >
              Back to sign in
            </button>
          )}
          {(mode === "login" || mode === "register") && (
            <button
              onClick={() => {
                setMode((m) => (m === "login" ? "register" : "login"));
                setFormError("");
              }}
              data-testid="auth-mode-toggle"
              className="mt-4 font-mono text-xs font-bold uppercase tracking-widest text-black/50 underline underline-offset-4 hover:text-primary"
            >
              {mode === "login" ? "New here? Create an account" : "Already enlisted? Sign in"}
            </button>
          )}
          </>
          )}
        </div>
      </main>
    );
  }

  const { user } = auth;
  return (
    <main data-testid="account-page" className="pt-16 min-h-screen">
      <div className="px-6 sm:px-12 py-12 border-b-2 border-black flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-16 w-16 border-2 border-black object-cover" data-testid="account-avatar" />
          ) : (
            <div className="h-16 w-16 border-2 border-black bg-accent flex items-center justify-center font-display text-2xl">
              {user.name?.[0] || "?"}
            </div>
          )}
          <div>
            <h1 className="font-display uppercase text-3xl sm:text-4xl tracking-tighter" data-testid="account-name">{user.name}</h1>
            <p className="font-mono text-xs text-black/50" data-testid="account-email">{user.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="account-logout"
          className="inline-flex items-center gap-2 border-2 border-black px-5 py-3 font-display uppercase text-lg shadow-hard btn-press hover:bg-accent"
        >
          <LogOut size={18} /> Sign out
        </button>
      </div>

      <div className="px-6 sm:px-12 py-12">
        {referral && (
          <div data-testid="referral-card" className="mb-12 border-2 border-black bg-white p-6 sm:p-8 shadow-hard max-w-2xl">
            <p className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-primary">
              <Gift size={16} /> Recruit &amp; get rewarded
            </p>
            <h2 className="mt-2 font-display uppercase text-3xl tracking-tighter">Give 10%, get 10%</h2>
            <p className="mt-3 font-mono text-sm text-black/60 leading-relaxed">
              Share your code. A friend gets 10% off their first order — and the moment it lands,
              a one-time 10% code hits your inbox. Every conversion earns another.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span data-testid="referral-code" className="border-2 border-black bg-background px-5 py-3 font-mono text-lg font-bold tracking-[0.2em]">
                {referral.code}
              </span>
              <button
                data-testid="referral-copy-button"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/shop?ref=${referral.code}`);
                  toast.success("Link copied. Go recruit.");
                }}
                className="inline-flex items-center gap-2 border-2 border-black bg-black px-5 py-3 font-display uppercase text-lg text-background shadow-hard btn-press hover:bg-primary"
              >
                <Copy size={16} /> Copy link
              </button>
            </div>
            <p className="mt-4 font-mono text-xs text-black/50" data-testid="referral-stats">
              {referral.uses} {referral.uses === 1 ? "recruit" : "recruits"} converted · {referral.rewards?.length || 0} reward {referral.rewards?.length === 1 ? "code" : "codes"} earned
            </p>
          </div>
        )}
        <h2 className="font-display uppercase text-3xl tracking-tight mb-8">Your orders</h2>
        {!emailVerified && (
          <div data-testid="verify-email-notice" className="mb-8 border-2 border-black bg-accent/60 p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-mono text-sm font-bold uppercase tracking-wider">Confirm your email to unlock order history</p>
              <p className="font-mono text-xs text-black/70 mt-1">We sent a confirmation link to {auth.user.email}. Until you click it, past orders stay private — even from someone who knows your address.</p>
            </div>
            <button
              data-testid="resend-verification-btn"
              onClick={resendVerification}
              disabled={resendBusy}
              className="border-2 border-black bg-black px-5 py-3 font-mono text-xs font-bold uppercase text-background shadow-hard btn-press hover:bg-primary disabled:opacity-50"
            >
              {resendBusy ? "Sending…" : "Resend the link"}
            </button>
          </div>
        )}
        {orders.length === 0 && emailVerified && (
          <div data-testid="account-no-orders">
            <p className="font-mono text-sm text-black/60">Nothing yet. The print queue awaits your first act of dissent.</p>
            <Link
              to="/shop"
              data-testid="account-shop-link"
              className="mt-6 inline-block border-2 border-black bg-primary px-8 py-4 font-display uppercase text-xl text-white shadow-hard btn-press hover:bg-black"
            >
              Fix that
            </Link>
          </div>
        )}
        <div className="space-y-4">
          {orders.map((o) => (
            <Reveal key={o.id}>
              <div data-testid={`account-order-${o.order_number}`} className="border-2 border-black p-5 shadow-hard bg-background flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-mono text-sm font-bold">{o.order_number}</p>
                  <p className="font-mono text-xs text-black/60 mt-1">
                    {o.items.map((i) => `${i.name} (${i.color} / ${i.size} × ${i.qty})`).join(", ")}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm font-bold">${o.total.toFixed(2)}</p>
                  <p className="mt-1 inline-block border-2 border-black bg-accent px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest">
                    {o.fulfillment_status === "shipped" ? "shipped" : o.status}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </main>
  );
}
